'use client'

import { useState } from 'react'

import { Loader2 } from 'lucide-react'

import { getNewsletterApiUrl } from '../../lib/lead-api'
import { Button } from '../ui/button'

type KineticNewsletterFormProps = {
  className?: string
  emailPlaceholder?: string
  submitLabel?: string
}

/**
 * POSTs JSON to the same API Gateway account as the contact form (`getNewsletterApiUrl()`).
 * Override with `NEXT_PUBLIC_NEWSLETTER_API_URL` if the route differs.
 */
export function KineticNewsletterForm({
  className = '',
  emailPlaceholder = 'Enter your work email',
  submitLabel = 'Subscribe',
}: KineticNewsletterFormProps) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState('')

  const validate = (value: string) => {
    const t = value.trim()
    if (!t) return 'Email is required'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return 'Enter a valid email address'
    return ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const err = validate(email)
    setError(err)
    setMessage('')
    if (err) {
      setStatus('idle')
      return
    }

    setStatus('submitting')
    const endpoint = getNewsletterApiUrl()

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          formType: 'newsletter',
          email: email.trim(),
          source: 'kinetic_newsletter',
        }),
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      await response.json().catch(() => ({}))
      setStatus('success')
      setMessage("Thanks. You're on the list.")
      setEmail('')
    } catch {
      setStatus('error')
      setMessage('Something went wrong. Try again or use Contact us.')
    }
  }

  if (status === 'success') {
    return (
      <p
        className={`text-center font-mono text-sm text-kinetic-primary-container ${className}`}
        role="status"
      >
        {message}
      </p>
    )
  }

  return (
    <div className={className}>
      <form
        onSubmit={handleSubmit}
        className="flex flex-col justify-center gap-3 sm:flex-row"
        noValidate
      >
        <div className="flex flex-col gap-1">
          <input
            type="email"
            name="email"
            value={email}
            onChange={ev => {
              setEmail(ev.target.value)
              if (error) setError('')
              if (status === 'error') setStatus('idle')
            }}
            autoComplete="email"
            placeholder={emailPlaceholder}
            disabled={status === 'submitting'}
            aria-invalid={!!error}
            aria-describedby={error ? 'newsletter-email-error' : undefined}
            className="border-kinetic-outline-variant/30 min-w-[220px] rounded-none border bg-kinetic-surface-container-lowest px-5 py-3 text-sm text-kinetic-on-surface placeholder:text-kinetic-outline disabled:opacity-60"
          />
          {error ? (
            <p id="newsletter-email-error" className="text-xs text-red-400 sm:px-1">
              {error}
            </p>
          ) : null}
        </div>
        <Button
          type="submit"
          variant="kinetic"
          size="lg"
          className="shrink-0 px-6 py-3 text-sm"
          disabled={status === 'submitting'}
        >
          {status === 'submitting' ? (
            <>
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />
              Sending…
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </form>
      {status === 'error' && message ? (
        <p className="mt-2 text-center text-xs text-red-400" role="alert">
          {message}
        </p>
      ) : null}
    </div>
  )
}
